import { dom } from "@fortawesome/fontawesome-svg-core";
import { formatMessageTimes } from "../../utils/time-format";
import { formatBookingDatetime } from "../booking/booking-resource";
import { announceSentMessage } from "./chat-announcer";

export const initConversationMessageList = () => {
  const conversation = document.getElementById("conversation") as HTMLDivElement;

  if (!conversation) {
    return;
  }

  const messageListsScrollContent = conversation.querySelector(
    ".message-list .scroll-content",
  ) as HTMLDivElement;

  if (!messageListsScrollContent) {
    return;
  }

  // Submit a message
  const messagesWrapper = conversation.querySelector(".messages-wrapper") as HTMLDivElement;
  const scrollElement = conversation.querySelector(".scroll-content") as HTMLDivElement;

  updateScroll(messageListsScrollContent);
  updateBookingTimes(messagesWrapper);

  const submitMessageForm = document.getElementById("submit-message-form") as HTMLFormElement;
  const textarea = submitMessageForm.querySelector("textarea") as HTMLTextAreaElement;
  const submitButton = submitMessageForm.querySelector(".message-submit-btn") as HTMLButtonElement;
  const submitButtonIcon = submitButton.querySelector(".icon") as HTMLDivElement;
  const submitButtonSpinner = submitButton.querySelector(".spinner") as HTMLDivElement;

  const defaultLimit = window.APP_PROPS.default_items_limit;
  const inboxId = conversation.dataset.inboxId;
  const id = submitMessageForm.dataset.id;
  const viewId = submitMessageForm.dataset.viewId;
  const csrfToken = submitMessageForm.dataset.csrfToken as string;

  const messageLoader = conversation.querySelector(".message-loader") as HTMLDivElement;

  let nextPaginationResult: string | null = `?offset=${defaultLimit}&limit=${defaultLimit}`;

  const cannedResponses = conversation.querySelector(".canned-responses");

  submitButton.addEventListener("click", (e) => {
    e.preventDefault();

    submitMessageForm.reportValidity();

    const formTextarea = submitMessageForm.querySelector("textarea") as HTMLTextAreaElement;
    const content = formTextarea.value;

    if (content.length <= 0) {
      return;
    }

    textarea.disabled = true;

    submitButtonIcon.classList.add("d-none");
    submitButtonSpinner.classList.remove("d-none");

    submitButton.disabled = true;

    const formData = new FormData();
    formData.append("content", content);
    formData.append("_csrf_token", csrfToken);

    fetch(`/website-api/inbox/${id}/?view_id=${viewId}`, {
      credentials: "same-origin",
      method: "post",
      body: formData,
    })
      .then((response) => {
        if (response.status === 429) {
          textarea.disabled = false;
          submitButtonSpinner.classList.add("d-none");
          submitButtonIcon.classList.remove("d-none");
          submitButton.disabled = false;

          alert(submitMessageForm.dataset.rateLimitError ?? "");

          throw new Error("Too many requests");
        }
        if (response.status === 403) {
          textarea.disabled = false;
          submitButtonSpinner.classList.add("d-none");
          submitButtonIcon.classList.remove("d-none");
          submitButton.disabled = false;

          alert(submitMessageForm.dataset.proxyError ?? "");

          throw new Error("VPN Detected (403)");
        }

        if (response.status >= 500) {
          textarea.disabled = false;
          submitButtonSpinner.classList.add("d-none");
          submitButtonIcon.classList.remove("d-none");
          submitButton.disabled = false;

          alert(submitMessageForm.dataset.genericError ?? "");

          throw new Error("500 problem.");
        }

        return response.json();
      })
      .then((jsonResponse) => {
        if (jsonResponse.metadata.code >= 400) {
          textarea.disabled = false;
          submitButtonSpinner.classList.add("d-none");
          submitButtonIcon.classList.remove("d-none");
          submitButton.disabled = false;

          if (jsonResponse.metadata.errors) {
            alert(jsonResponse.metadata.errors[0].error);
            return;
          }

          if (jsonResponse.metadata.error_message) {
            alert(jsonResponse.metadata.error_message);
            return;
          }

          alert(submitMessageForm.dataset.genericError ?? "");
          return;
        }

        textarea.value = "";
        textarea.disabled = false;
        submitButtonSpinner.classList.add("d-none");
        submitButtonIcon.classList.remove("d-none");
        submitButton.disabled = false;

        textarea.focus();

        /**
         * Remove textarea value from cookie, we do not need to display it to the user in the future anymore
         */
        const hostnameBase = window.location.hostname.replace(/^[^.]*./, "");
        // biome-ignore lint/suspicious/noDocumentCookie: <Refactor later>
        document.cookie =
          "conversation-" +
          inboxId +
          "-textarea=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=" +
          hostnameBase +
          "; secure; samesite=lax";

        const hasAlreadyMessage = document.querySelector(
          `.message-container[data-id='${jsonResponse.data.id}']`,
        );

        // Check if Ajax already delivered this message
        if (hasAlreadyMessage) {
          announceSentMessage();
          return;
        }

        // The list runs oldest first: the newest message goes last.
        messagesWrapper.insertAdjacentHTML("beforeend", jsonResponse.data.rendered);
        announceSentMessage();
        formatMessageTimes(messagesWrapper);
        scrollElement.scrollTop = scrollElement.scrollHeight;

        if (cannedResponses) {
          cannedResponses.classList.add("d-none");
        }

        // Tell FontAwesome to search for new icons.
        // Fix the bug with reviews icons not being rendered.
        dom.i2svg();
      });
  });

  const isContentScrollable = (): boolean => {
    // Check if content height exceeds container height (is scrollbar present?)
    return scrollElement.scrollHeight > scrollElement.clientHeight;
  };

  // One page at a time: every scroll event at the top asks again (a trackpad
  // bounce fires several), and each would insert the same page
  let loadingPage = false;
  // A page that has not arrived after this gives up, so scrolling to the top can ask again
  const PAGE_TIMEOUT_MS = 30000;

  const loadNextPage = () => {
    if (!nextPaginationResult || loadingPage) {
      return;
    }

    loadingPage = true;
    messageLoader.classList.remove("d-none");

    let url = `/website-api/inbox/${inboxId}/${nextPaginationResult}`;
    const params = new URLSearchParams(window.location.search);

    if (params.has("view_id")) {
      url += `&view_id=${params.get("view_id")}`;
    }

    // AbortController, not AbortSignal.timeout(): that one is missing before Safari 16
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PAGE_TIMEOUT_MS);

    fetch(url, {
      credentials: "same-origin",
      method: "get",
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then(
        (json) => {
          clearTimeout(timeout);
          loadingPage = false;

          if (json.metadata.code !== 200) {
            // Target language invalid
            messageLoader.classList.add("d-none");
            alert(json.metadata.error_message);
            return;
          }

          if (!json.data.rendered) {
            // If current page has no visible items, automatically load next page
            messageLoader.classList.add("d-none");
            nextPaginationResult = json.pagination.next_results;
            if (nextPaginationResult) {
              loadNextPage();
            }
            return;
          }

          // Pages are counted from the newest message, so the ones sent or received since
          // the chat opened push messages already shown into this page: leave those out.
          const page = document.createElement("template");
          page.innerHTML = json.data.rendered;
          const shown = new Set(
            [...messagesWrapper.querySelectorAll<HTMLElement>(".message-container[data-id]")].map(
              (message) => message.dataset.id,
            ),
          );

          for (const message of page.content.querySelectorAll<HTMLElement>(
            ".message-container[data-id]",
          )) {
            if (shown.has(message.dataset.id)) {
              message.remove();
            }
          }

          nextPaginationResult = json.pagination.next_results;

          // The whole page was on screen already: go on with the next one, as for an empty page
          if (page.content.querySelector(".message-container") === null) {
            messageLoader.classList.add("d-none");
            if (nextPaginationResult) {
              loadNextPage();
            }
            return;
          }

          // Keep the view where it was: the distance to the bottom of the list does not
          // change when older messages go in above it. This holds whether or not the
          // browser's own scroll anchoring already moved the view.
          const distanceFromBottom = scrollElement.scrollHeight - scrollElement.scrollTop;

          // The list runs oldest first, and the page holds older messages: they go first.
          messagesWrapper.prepend(page.content);

          updateBookingTimes(messagesWrapper);
          formatMessageTimes(messagesWrapper);

          // The loader sits above the list too, so it hides before the view is put back.
          const holdView = () => {
            scrollElement.scrollTop = scrollElement.scrollHeight - distanceFromBottom;
          };
          messageLoader.classList.add("d-none");
          holdView();
          const heldAt = scrollElement.scrollTop;

          // Tell FontAwesome to search for new icons.
          // Fix the bug with reviews icons not being rendered.
          // The icons are drawn a moment later and change the height above the view:
          // put it back again then (Safari does not anchor the scroll by itself), unless
          // the view moved meanwhile (a new message, a scroll, the browser's own anchoring)
          dom
            .i2svg({ node: messagesWrapper })
            .then(() => {
              // Within a pixel: zoomed and high-density screens round the scroll position
              if (Math.abs(scrollElement.scrollTop - heldAt) <= 1) {
                holdView();
              }
            })
            .catch(() => {
              // Icons that did not draw leave the view where it is
            });

          // If content still doesn't fill viewport, load more automatically
          if (!isContentScrollable() && nextPaginationResult) {
            loadNextPage();
          }
        },
        // Only the request and its body: an error in the handler above stays unhandled,
        // so it still reaches Sentry
        () => {
          // Offline, too slow or a broken response: scrolling to the top again asks again
          clearTimeout(timeout);
          loadingPage = false;
          messageLoader.classList.add("d-none");
        },
      );
  };

  messageListsScrollContent.addEventListener("scroll", (e: Event) => {
    const target = e.target as HTMLDivElement;

    if (target.scrollTop !== 0) {
      return;
    }

    loadNextPage();
  });

  // Check if initial content is scrollable, if not load more pages
  setTimeout(() => {
    if (!isContentScrollable() && nextPaginationResult) {
      loadNextPage();
    }
  }, 100);

  function updateScroll(messageListsScrollContent: HTMLDivElement) {
    messageListsScrollContent.scrollTop = messageListsScrollContent.scrollHeight;

    // Reveal after scroll settles to avoid flash of wrong scroll position
    setTimeout(() => {
      messageListsScrollContent.scrollTop = messageListsScrollContent.scrollHeight;
      messageListsScrollContent.classList.remove("invisible");
      messageListsScrollContent.parentElement?.querySelector(".message-initial-loader")?.remove();
    }, 0);
  }

  function updateBookingTimes(messageListsScrollContent: HTMLDivElement) {
    const bookingDatetimeWrappers: NodeListOf<HTMLElement> =
      messageListsScrollContent.querySelectorAll(".booking-datetime-wrapper");

    bookingDatetimeWrappers.forEach((wrapper) => {
      formatBookingDatetime(wrapper);
    });
  }
};
